import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import RecommendationTip from '../components/RecommendationTip';
import Modal from '../components/Modal';
import '../styles/OwnershipScorecard.scss';
import { themeVars } from '../config/theme';

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);

const rowClass = (actual, target) => {
  if (actual === null || actual === 0) return 'sc-row--empty';
  return actual >= target ? 'sc-row--achieved' : 'sc-row--partial';
};

const OwnershipScorecard = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [filename, setFilename] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [noDataSheet, setNoDataSheet] = useState(false);
  const [CombinedPoints, setCombinedPoints] = useState(null);
  const [maxCombinedPoints, setMaxCombinedPoints] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [entityValueModal, setEntityValueModal] = useState(false);


  const applyData = (data) => {
    setEntityValueModal(!!data.entity_value_missing);  // shown every time the scorecard loads
    setResults(data.results);
    setTotalPoints(data.total_points);
    setFilename(data.filename);
    setIndicators(data.indicators || []);  //To pull indicators from backend instead of it being hardcoded in the frontend    
    setNoDataSheet(false);
    setError(null);
    setCombinedPoints(null);
  };

  const fetchCombinedTotal = () => {
    api.get('/core/scorecard/combined-total/')
      .then((res) => {
        setCombinedPoints(res.data.combined_points);
        if (res.data.max_points != null) setMaxCombinedPoints(res.data.max_points);
      })
      .catch(() => {});
  };

  const calculateFromDataSheet = async () => {
    setCalculating(true);
    setError(null);
    try {
      const res = await api.post('/core/scorecard/calculate_from_data_sheet/', { element: 'ownership' });
      applyData(res.data);
      fetchCombinedTotal();
    } catch (err) {
      if (err.response?.status === 404) setNoDataSheet(true);
      else setError(err.response?.data?.error || 'Failed to calculate from data sheet. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    api.get('/core/scorecard/get_scorecard/?element=ownership')
      .then((res) => { applyData(res.data); setLoading(false); })
      .catch((err) => {
        setLoading(false);
        if (err.response?.data?.indicators?.length) setIndicators(err.response.data.indicators);
        if (err.response?.status === 404) calculateFromDataSheet();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCombinedTotal(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const resultMap = results
    ? Object.fromEntries(results.map((r) => [r.id, r]))
    : {};

  const maxPoints = indicators.reduce((s, ind) => s + (ind.points || 0), 0);

  let lastSection = null;

  return (
    <div className="sc-container themed" style={themeVars('ownership')}>
      <div className="sc-header">
        <button className="sc-back" onClick={() => navigate('/ownership')}>← Back</button>
        <div>
          <h1>Ownership Scorecard</h1>
          <p>B-BBEE — Ownership Element Calculation</p>
        </div>
      </div>

      <div className="sc-upload-bar">
        <div className="sc-upload-info">
          {loading
            ? <span className="sc-upload-hint">Loading…</span>
            : filename
              ? <span className="sc-upload-name">✓ Calculated from: <strong>{filename}</strong></span>
              : <span className="sc-upload-hint">No data sheet found</span>
          }
        </div>
        <div className="sc-upload-actions">
          <button
            className="sc-template-btn"
            onClick={() => navigate('/ownership/documentation')}
          >
            Go to Documentation
          </button>
          <button
            className="sc-upload-btn"
            onClick={calculateFromDataSheet}
            disabled={calculating || loading}
          >
            {calculating ? 'Calculating…' : 'Recalculate'}
          </button>
        </div>
      </div>

      {noDataSheet && (
        <div className="sc-error">
          No Ownership Data Sheet uploaded yet. Go to{' '}
          <button className="sc-error-link" onClick={() => navigate('/ownership/documentation')}>
            Documentation
          </button>{' '}
          and upload the <strong>Ownership Data Sheet</strong> Excel file.
        </div>
      )}

      {error && <div className="sc-error">{error}</div>}

      <Modal
        isOpen={entityValueModal}
        onClose={() => setEntityValueModal(false)}
        title="⚠ Value of Entity Not Indicated"
      >
        The value of the entity (Market value of Generic Equity, cell C7) has not been indicated
        in the Ownership Data Sheet, so Net Value cannot be calculated and scores 0. Update the
        sheet and re-upload it from Documentation.
      </Modal>

      <div className="sc-table-outer">
        <div className="sc-table-wrapper">
          <table className="sc-table">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Compliance Target</th>
                <th>Max Points</th>
                <th>Actual (%)</th>
                <th>Points Achieved</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((ind) => {
                const showHeader = ind.section && ind.section !== lastSection;
                lastSection = ind.section;

                const r = resultMap[ind.id];
                const actual = r?.actual ?? null;
                const pts = r?.points ?? 0;
                const isMax = pts >= ind.points;
                return (
                  <React.Fragment key={ind.id}>
                    {showHeader && (
                      <tr className="sc-row--section-header">
                        <td colSpan="5">{ind.section}</td>
                      </tr>
                    )}

                  <tr key={ind.id} className={ind.id === 'net_value' && r?.sub_minimum_met === false ? 'sc-row--submin-fail' : rowClass(actual, ind.target)}>
                    <td>{ind.label}</td>
                    <td>{ind.targetLabel ?? `${ind.target}%`}</td>
                    <td>{ind.points}</td>
                    <td>{actual !== null ? `${fmt(actual)}%` : '—'}</td>
                    <td>
                      <span className={`sc-points${isMax ? ' sc-points--max' : pts === 0 ? ' sc-points--zero' : ''}`}>
                        {fmt(pts)}
                      </span>
                      <RecommendationTip ind={ind} actual={actual} pts={pts} spendRow={undefined} />
                    </td>
                  </tr>
                </React.Fragment>
                );
              })}
              <tr className="sc-total-row">
                <td colSpan="3">Total</td>
                <td />
                <td>{fmt(totalPoints)} / {maxPoints || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {results?.some(r => r.sub_minimum_met === false) && (
          <div className="sc-submin-icon-wrap">
            <span className="sc-submin-icon-btn">⚠</span>
            <div className="sc-submin-popup">
              Sub-minimum not met — overall scorecard level will be discounted by one level.
            </div>
          </div>
        )}
      </div>

      <div className="sc-summary">
        <div className="sc-summary-card sc-summary-card--total">
          <div className="sc-summary-score">{fmt(totalPoints)} <span>/ {maxPoints || '—'}</span></div>
          <div className="sc-summary-label">Total Ownership Points</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{CombinedPoints !== null ? fmt(CombinedPoints) : '-'} <span>/ {maxCombinedPoints ?? '—'}</span></div>
          <div className="sc-summary-label">Points (incl. bonus)</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : '—'}<span>%</span></div>
          <div className="sc-summary-label">Ownership Achievement</div>
        </div>
      </div>
    </div>
  );
};

export default OwnershipScorecard;
